import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Wraps a horizontally scrolling tab strip. On a phone (this project's `md:` is the narrow side)
 * a soft fade shows on whichever edge has more tabs beyond it, so a person knows to swipe.
 */
export function ScrollEdgeFade({ children }: { children: ReactNode }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = wrap.current?.querySelector<HTMLElement>('[role="tablist"]');
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    measure();
    const el = wrap.current?.querySelector<HTMLElement>('[role="tablist"]');
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return (
    <div ref={wrap} className="relative" onScrollCapture={measure}>
      {children}
      <div
        aria-hidden
        data-testid="tabs-fade-left"
        className={`pointer-events-none absolute left-0 top-0 hidden h-12 w-16 items-center justify-start bg-gradient-to-r from-white from-40% to-transparent md:flex ${edges.left ? "opacity-100" : "opacity-0"}`}
      >
        <ChevronLeft className="h-5 w-5 text-[#165FE3]" />
      </div>
      <div
        aria-hidden
        data-testid="tabs-fade-right"
        className={`pointer-events-none absolute right-0 top-0 hidden h-12 w-16 items-center justify-end bg-gradient-to-l from-white from-40% to-transparent md:flex ${edges.right ? "opacity-100" : "opacity-0"}`}
      >
        <ChevronRight className="h-5 w-5 text-[#165FE3]" />
      </div>
    </div>
  );
}
